// Shared mapping for the product bulk-update endpoint (PATCH admin/products/bulk-update).
//
// Two callers consume this:
//   - The editable grid (BulkEditApi) — sends only the cells the user edited
//     per row via buildPatchChange().
//   - The "Update from File" flow (BulkUpdateFromFile) — has no per-cell dirty
//     signal, so it writes every recognized, non-empty column of an uploaded
//     spreadsheet row via buildFileChange().
//
// Keeping the wire contract (field names, numeric coercion, status/b2b ints,
// relationship *_ids arrays) in one place so the two paths can't drift apart.

// Caps the request size; larger dirty sets are sent as sequential batches.
export const PATCH_BATCH_SIZE = 500;

// Price scopes are permission-gated. Editing these without EDIT on the scope
// makes the backend 422 the whole bulk-update, so callers strip them up front.
export const COST_FIELD_KEYS = new Set(["cost", "profit", "profit_margin"]);
export const REGULAR_PRICE_FIELD_KEYS = new Set(["regular_price"]);
export const SALE_PRICE_FIELD_KEYS = new Set(["sale_price"]);

export const PATCH_SCALAR_FIELDS = new Set([
  "name", "ar_name", "description", "ar_description",
  "barcode", "color", "size", "reference_link",
  "product_option_type", "has_product_option",
  "quantity", "height", "width", "position", "order_in_view",
  "cost", "regular_price", "sale_price",
]);
export const PATCH_NUMERIC_FIELDS = new Set([
  "quantity", "height", "width", "position", "order_in_view",
  "cost", "regular_price", "sale_price",
]);
// Fields where emptying the cell means "unset this column", sent as an
// explicit null. The API only honours null for fields it marks clearable
// (see PRODUCT_FIELD_SCHEMA server-side) — everything else treats a blank
// as "leave it alone", which is what stops a sheet missing a column from
// wiping that column across the catalogue.
export const PATCH_CLEARABLE_FIELDS = new Set(["position"]);
// Status values the user can choose. "Delete" is handled separately via
// admin/products/bulk-delete and never sent to bulk-update.
export const STATUS_NAME_TO_INT = { Active: 1, Inactive: 2 };
// Grid column key → wire field for *_ids relationship arrays.
export const RELATIONSHIP_COL_TO_WIRE_KEY = {
  brand: "brand_ids",
  category: "category_ids",
  sub_category: "sub_category_ids",
  occasion: "occasion_ids",
};

export const namesToIds = (commaString, options) => {
  if (!options) return [];
  return String(commaString || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((name) => options.find((o) => o.name === name)?.id)
    .filter((id) => Number.isInteger(id));
};

// Build one entry of the `changes` array from a grid row + the set of edited
// keys. Only edited keys are included so omitted fields stay untouched
// server-side. Returns null if the row has no persisted int id (new/duplicate
// rows can't be inserted via bulk-update — caller surfaces them separately).
export const buildPatchChange = (row, editedKeys, dropdownOptions) => {
  if (!Number.isInteger(row.id)) return null;
  const change = { id: row.id };
  for (const key of editedKeys) {
    if (key === "image") continue; // images go through PUT /admin/products/
    if (PATCH_SCALAR_FIELDS.has(key)) {
      const raw = row[key];
      if (PATCH_NUMERIC_FIELDS.has(key)) {
        // Emptying a clearable cell must send an explicit null — that is
        // what the API reads as "unset this column". Special-cased because
        // `Number("")` is 0, not NaN, so the generic branch below would
        // turn a cleared `position` into 0 — and 0 sorts *above* 1, pinning
        // the product to the very top of the storefront instead of
        // unfeaturing it. Non-clearable numerics keep their existing
        // blank -> 0 behaviour.
        const blank = raw === undefined || raw === null || String(raw).trim() === "";
        if (blank && PATCH_CLEARABLE_FIELDS.has(key)) {
          change[key] = null;
        } else {
          const num = Number(raw);
          change[key] = Number.isFinite(num) ? num : null;
        }
      } else {
        change[key] = raw === undefined || raw === "" ? null : raw;
      }
    } else if (key === "type") {
      const match = dropdownOptions.type?.find((t) => t.name === row.type);
      if (match) change.type_id = match.id;
    } else if (key === "is_b2b") {
      change.is_b2b = row.is_b2b === "yes" ? 1 : 0;
    } else if (key === "status") {
      const intVal = STATUS_NAME_TO_INT[row.status];
      if (intVal != null) change.status = intVal;
    } else if (RELATIONSHIP_COL_TO_WIRE_KEY[key]) {
      change[RELATIONSHIP_COL_TO_WIRE_KEY[key]] = namesToIds(
        row[key],
        dropdownOptions[key]
      );
    }
    // ar_*, new_from, new_to, ar_category, etc. — not in endpoint contract; skipped.
  }
  return change;
};

// ---------------------------------------------------------------------------
// File-upload path
// ---------------------------------------------------------------------------

// Spreadsheet column names (lowercased/alias-normalized by parseProductsFile)
// the file-update flow will write. Deliberately a subset of the grid's columns:
//
//   - sub_category / occasion are excluded — resolving their names to ids needs
//     a complete option list the page doesn't load (category/brand/type lists
//     are loaded in full; sub-categories are only fetched per selected category).
//     Including them would risk clearing the relationship from an unmatched name.
//   - ar_category / ar_sub_category / ar_type are display-only (not in the wire
//     contract); `type` carries the Arabic type via type_id.
//   - image is uploaded through a different endpoint.
export const UPDATABLE_FILE_COLUMNS = new Set([
  "name", "ar_name", "description", "ar_description", "barcode",
  "color", "size", "reference_link",
  "quantity", "height", "width", "position", "order_in_view",
  "cost", "regular_price", "sale_price",
  "type", "is_b2b", "status", "brand", "category",
]);

const FILE_PLAIN_SCALAR = new Set([
  "name", "ar_name", "description", "ar_description",
  "color", "size", "reference_link",
]);
const FILE_NUMERIC = new Set([
  "quantity", "height", "width", "position", "order_in_view",
  "cost", "regular_price", "sale_price",
]);

const TRUTHY = new Set(["yes", "y", "true", "1"]);
const FALSY = new Set(["no", "n", "false", "0"]);
const STATUS_TEXT_TO_INT = { active: 1, inactive: 2 };

const norm = (v) => (v == null ? "" : String(v).trim());

// Resolve a comma-separated list of taxonomy names to ids, reporting any names
// that don't match a known option so the caller can warn instead of silently
// clearing the relationship (sending [] would wipe it server-side).
const resolveNames = (raw, options = [], { matchArName = false } = {}) => {
  const names = norm(raw)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const ids = [];
  const missing = [];
  for (const nm of names) {
    const opt = options.find(
      (o) => o.name === nm || (matchArName && o.ar_name === nm)
    );
    if (opt && Number.isInteger(opt.id)) ids.push(opt.id);
    else missing.push(nm);
  }
  return { ids, missing };
};

// Map ONE parsed spreadsheet row to a bulk-update `change`.
//
// Identifier: a numeric `id` wins; otherwise a non-empty `barcode` is sent as
// the matcher (backend resolves barcode → product — see the bulk-update file
// contract). A row with neither is returned as { skip: true }.
//
// Every recognized, non-empty column becomes a field. Empty cells are skipped —
// the file never clears a field from a blank, and a value that can't be parsed
// (bad number / unknown status / unresolved taxonomy name) is reported and
// omitted rather than sent as null.
export function buildFileChange(row, ctx = {}) {
  const {
    dropdownOptions = {},
    canEditCost = true,
    canEditRegularPrice = true,
    canEditSalePrice = true,
  } = ctx;

  const idRaw = norm(row.id);
  const idNum = idRaw !== "" && Number.isFinite(Number(idRaw)) ? Number(idRaw) : null;
  const barcodeRaw = norm(row.barcode);

  const change = {};
  let identifier;
  let matchedBy;
  if (idNum != null && Number.isInteger(idNum)) {
    change.id = idNum;
    identifier = `#${idNum}`;
    matchedBy = "id";
  } else if (barcodeRaw !== "") {
    change.barcode = barcodeRaw;
    identifier = barcodeRaw;
    matchedBy = "barcode";
  } else {
    return { skip: true, reason: "No id or barcode — can't match a product." };
  }

  const updatedKeys = [];
  const unresolved = []; // [{ field, values: [] }] — names that didn't match
  const invalid = []; // [{ field, value }] — unparseable number / enum
  const permissionStripped = [];

  const applyB2b = (raw) => {
    const v = norm(raw).toLowerCase();
    if (TRUTHY.has(v)) { change.is_b2b = 1; updatedKeys.push("is_b2b"); }
    else if (FALSY.has(v)) { change.is_b2b = 0; updatedKeys.push("is_b2b"); }
    else invalid.push({ field: "is_b2b", value: norm(raw) });
  };

  for (const col of UPDATABLE_FILE_COLUMNS) {
    // When the row is matched by barcode, the barcode cell is the matcher — not
    // also an editable field.
    if (col === "barcode" && matchedBy === "barcode") continue;
    if (!(col in row)) continue;
    const raw = row[col];
    if (norm(raw) === "") continue;

    if (col === "cost" && !canEditCost) { permissionStripped.push("cost"); continue; }
    if (col === "regular_price" && !canEditRegularPrice) { permissionStripped.push("regular_price"); continue; }
    if (col === "sale_price" && !canEditSalePrice) { permissionStripped.push("sale_price"); continue; }

    if (FILE_PLAIN_SCALAR.has(col)) {
      change[col] = String(raw);
      updatedKeys.push(col);
    } else if (col === "barcode") {
      change.barcode = norm(raw);
      updatedKeys.push("barcode");
    } else if (FILE_NUMERIC.has(col)) {
      const n = Number(raw);
      if (Number.isFinite(n)) { change[col] = n; updatedKeys.push(col); }
      else invalid.push({ field: col, value: norm(raw) });
    } else if (col === "type") {
      const { ids, missing } = resolveNames(raw, dropdownOptions.type, { matchArName: true });
      if (ids.length > 0) { change.type_id = ids[0]; updatedKeys.push("type"); }
      if (missing.length) unresolved.push({ field: "type", values: missing });
    } else if (col === "brand") {
      const { ids, missing } = resolveNames(raw, dropdownOptions.brand);
      if (ids.length) { change.brand_ids = ids; updatedKeys.push("brand"); }
      if (missing.length) unresolved.push({ field: "brand", values: missing });
    } else if (col === "category") {
      const { ids, missing } = resolveNames(raw, dropdownOptions.category);
      if (ids.length) { change.category_ids = ids; updatedKeys.push("category"); }
      if (missing.length) unresolved.push({ field: "category", values: missing });
    } else if (col === "is_b2b") {
      applyB2b(raw);
    } else if (col === "status") {
      const v = norm(raw).toLowerCase();
      if (v in STATUS_TEXT_TO_INT) { change.status = STATUS_TEXT_TO_INT[v]; updatedKeys.push("status"); }
      else invalid.push({ field: "status", value: norm(raw) });
    }
  }

  // "b2b" header variant (parseProductsFile doesn't alias it to is_b2b).
  if (!updatedKeys.includes("is_b2b") && "b2b" in row && norm(row.b2b) !== "") {
    applyB2b(row.b2b);
  }

  // Nothing to write beyond the identifier — surface as skipped so the caller
  // doesn't send a no-op change.
  const fieldCount = Object.keys(change).length - 1; // minus id/barcode matcher
  if (fieldCount <= 0) {
    return {
      skip: true,
      reason: "No updatable, non-empty columns.",
      identifier, matchedBy, unresolved, invalid, permissionStripped,
    };
  }

  return { change, identifier, matchedBy, updatedKeys, unresolved, invalid, permissionStripped };
}
